import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import { ArrowLeft, History, HelpCircle, Loader2, Sparkles, Volume2, VolumeX, Clock, Share2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const A = '/roleta';

// centro de cada setor no disco (graus, sentido horário a partir do topo)
const SECTOR_ANGLE = { WIN_100X: 0, LOSE: 120, WIN_2X: 240 };
const RESULT_IMG = { WIN_100X: `${A}/reward_100x.png`, WIN_2X: `${A}/reward_2x.png`, LOSE: `${A}/try_again.png` };

const fmtN = (v) => Number(v || 0).toLocaleString('pt-BR');
const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  } catch { return '-'; }
};

const RESULT_LABEL = {
  WIN_100X: { txt: '100X — 50.000 moedas!', cls: 'text-yellow-300' },
  WIN_2X: { txt: '2X — 1.000 moedas', cls: 'text-emerald-300' },
  LOSE: { txt: 'Tente novamente', cls: 'text-blue-200' },
};

const FULFILL_LABEL = {
  DELIVERED: { txt: 'Entregue', cls: 'bg-emerald-500/20 text-emerald-300' },
  PENDING: { txt: 'Creditando...', cls: 'bg-yellow-500/20 text-yellow-300' },
  PROCESSING: { txt: 'Creditando...', cls: 'bg-yellow-500/20 text-yellow-300' },
  FAILED: { txt: 'Reprocessando', cls: 'bg-orange-500/20 text-orange-300' },
  MANUAL_REVIEW: { txt: 'Em análise', cls: 'bg-orange-500/20 text-orange-300' },
};

const CoinRain = () => {
  const coins = React.useMemo(() => {
    const seed = [];
    for (let i = 0; i < 30; i++) {
      seed.push({
        left: (i * 37) % 100,
        delay: ((i * 13) % 20) / 10,
        dur: 2.2 + ((i * 7) % 14) / 10,
        size: 26 + ((i * 11) % 26),
        spin: i % 2 ? 1 : -1,
      });
    }
    return seed;
  }, []);
  return (
    <div className="fixed inset-0 z-[60] pointer-events-none overflow-hidden" data-testid="coin-rain">
      {coins.map((c, i) => (
        <img key={i} src={`${A}/moeda_amarela.png`} alt=""
          className="absolute -top-16"
          style={{
            left: `${c.left}%`, width: c.size, height: c.size,
            animation: `coin-fall ${c.dur}s linear ${c.delay}s infinite`,
            '--spin-dir': c.spin,
          }} />
      ))}
    </div>
  );
};

const fmtAgo = (iso) => {
  try {
    const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return 'agora';
    if (s < 3600) return `${Math.floor(s / 60)} min atrás`;
    if (s < 86400) return `${Math.floor(s / 3600)} h atrás`;
    return `${Math.floor(s / 86400)} d atrás`;
  } catch { return ''; }
};

const PAGE_SIZE = 100;

const JackpotMarquee = () => {
  const [jackpots, setJackpots] = useState([]);

  useEffect(() => {
    let alive = true;
    const fetchJackpots = () => {
      axios.get(`${API}/roulette/jackpot-winners?limit=20`)
        .then((r) => { if (alive) setJackpots(r.data.winners || []); })
        .catch(() => {});
    };
    fetchJackpots();
    const timer = setInterval(fetchJackpots, 60000);
    return () => { alive = false; clearInterval(timer); };
  }, []);

  if (jackpots.length === 0) return null;
  return (
    <div className="relative overflow-hidden rounded-lg bg-gradient-to-r from-yellow-500/10 via-amber-500/20 to-yellow-500/10 border border-yellow-500/40 mb-3 py-1.5"
      data-testid="jackpot-marquee">
      <style>{'@keyframes rl-marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }'}</style>
      <div className="flex w-max whitespace-nowrap"
        style={{ animation: `rl-marquee ${Math.max(18, jackpots.length * 6)}s linear infinite` }}>
        {[0, 1].map((dup) => (
          <div key={dup} className="flex items-center">
            {jackpots.map((w, i) => (
              <span key={`${dup}-${w.spin_id || i}`} className="mx-5 text-xs font-black text-yellow-300 inline-flex items-center gap-1">
                👑 {w.name}
                <span className="text-yellow-500/80 font-normal">({w.kako_masked})</span>
                <span className="text-yellow-200">+{fmtN(w.prize)}</span>
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

const WinnersTicker = () => {
  const [winners, setWinners] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [filter, setFilter] = useState('WIN_2X');
  const filterRef = useRef('WIN_2X');
  const loadingRef = useRef(false);
  const wsRef = useRef(null);

  const dedupe = (list) => {
    const seen = new Set();
    return list.filter((w) => {
      if (seen.has(w.spin_id)) return false;
      seen.add(w.spin_id);
      return true;
    });
  };

  const loadMore = useCallback(async (offset, f) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    const result = f || filterRef.current;
    try {
      const r = await axios.get(`${API}/roulette/recent-winners?limit=${PAGE_SIZE}&offset=${offset}&result=${result}`);
      if (filterRef.current === result) {
        setWinners((prev) => dedupe(offset === 0 ? (r.data.winners || []) : [...prev, ...(r.data.winners || [])]));
        setHasMore(!!r.data.has_more);
      }
    } catch { /* noop */ }
    loadingRef.current = false;
  }, []);

  const switchFilter = (f) => {
    if (f === filterRef.current) return;
    filterRef.current = f;
    setFilter(f);
    setWinners([]);
    setHasMore(false);
    loadMore(0, f);
  };

  useEffect(() => {
    let alive = true;
    let pingTimer = null;
    let reconnectTimer = null;

    loadMore(0);

    const connect = () => {
      if (!alive) return;
      try {
        const ws = new WebSocket(`${BACKEND_URL.replace(/^http/, 'ws')}/api/ws/roulette-wins`);
        wsRef.current = ws;
        ws.onopen = () => {
          pingTimer = setInterval(() => { try { ws.send('ping'); } catch { /* noop */ } }, 25000);
        };
        ws.onmessage = (ev) => {
          if (ev.data === 'pong') return;
          try {
            const msg = JSON.parse(ev.data);
            if (msg.type === 'initial_data') {
              const list = (msg.winners || []).filter((w) => w.result === filterRef.current);
              setWinners((prev) => dedupe([...list, ...prev]));
            }
            if (msg.type === 'roulette_win' && msg.win && msg.win.result === filterRef.current) {
              setWinners((prev) => dedupe([msg.win, ...prev]));
            }
          } catch { /* mensagem não-JSON */ }
        };
        ws.onclose = () => {
          if (pingTimer) clearInterval(pingTimer);
          if (alive) reconnectTimer = setTimeout(connect, 8000);
        };
        ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
      } catch { reconnectTimer = setTimeout(connect, 8000); }
    };
    connect();

    return () => {
      alive = false;
      if (pingTimer) clearInterval(pingTimer);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try { wsRef.current?.close(); } catch { /* noop */ }
    };
  }, [loadMore]);

  // infinite scroll: chegou perto do fim → carrega mais 100
  const onScroll = (e) => {
    const el = e.currentTarget;
    if (hasMore && el.scrollTop + el.clientHeight >= el.scrollHeight - 60) {
      loadMore(winners.length);
    }
  };

  const filterTabs = (
    <div className="flex gap-1.5 mb-2">
      <button
        onClick={() => switchFilter('WIN_2X')}
        className={`flex-1 text-xs font-bold rounded-lg px-2 py-1.5 border transition-colors ${
          filter === 'WIN_2X'
            ? 'bg-blue-500/30 border-blue-400 text-white'
            : 'bg-white/5 border-white/10 text-blue-300/70 hover:border-blue-400/50'
        }`}
        data-testid="winners-filter-2x"
      >
        🎉 Prêmios 2X (1K)
      </button>
      <button
        onClick={() => switchFilter('WIN_100X')}
        className={`flex-1 text-xs font-black rounded-lg px-2 py-1.5 border transition-colors ${
          filter === 'WIN_100X'
            ? 'bg-yellow-500/25 border-yellow-400 text-yellow-200'
            : 'bg-white/5 border-white/10 text-yellow-300/60 hover:border-yellow-400/50'
        }`}
        data-testid="winners-filter-100x"
      >
        💎 JACKPOT 100X (50K)
      </button>
    </div>
  );

  if (winners.length === 0) {
    return (
      <div>
        {filterTabs}
        <p className="text-center text-xs text-blue-300/70 py-4" data-testid="live-winners-empty">
          {filter === 'WIN_100X'
            ? 'Nenhum Jackpot nesta edição ainda — pode ser você! 💎'
            : 'Nenhum prêmio ainda — gire e apareça aqui! 🍀'}
        </p>
      </div>
    );
  }

  return (
    <div>
      {filterTabs}
      <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1 custom-scrollbar"
        data-testid="live-winners-scroll" onScroll={onScroll}>
        {winners.map((w) => (
          <div key={w.spin_id} data-testid="live-winner-item"
            className={`flex items-center justify-between gap-2 rounded-lg px-3 py-1.5 animate-in slide-in-from-top-2 fade-in duration-500 ${
              w.result === 'WIN_100X'
                ? 'bg-yellow-500/10 border border-yellow-500/30'
                : 'bg-white/5 border border-white/10'
            }`}>
            <div className="min-w-0">
              <p className="text-sm font-bold text-white truncate">
                {w.name} <span className="text-blue-400 font-normal text-xs">({w.kako_masked})</span>
              </p>
              <p className="text-[10px] text-blue-300">{fmtAgo(w.at)}</p>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              {w.result === 'WIN_100X' && (
                <span className="text-[10px] font-black text-yellow-300 bg-yellow-500/20 border border-yellow-500/40 rounded px-1.5 py-0.5">100X!</span>
              )}
              <img src={`${A}/moeda_amarela.png`} alt="" className="w-5 h-5" />
              <span className="text-sm font-black text-yellow-300">+{fmtN(w.prize)}</span>
            </div>
          </div>
      ))}
        {hasMore && (
          <p className="text-center text-[10px] text-blue-300/70 py-1" data-testid="live-winners-loading-more">
            role para carregar mais...
          </p>
        )}
      </div>
    </div>
  );
};

const RankAvatar = ({ entry, size, border }) => (
  <div className={`${size} rounded-full overflow-hidden border-2 ${border} bg-blue-900 mb-1 shadow-lg flex-shrink-0`}>
    {entry.avatar ? (
      <img src={entry.avatar} alt="" className="w-full h-full object-cover" />
    ) : (
      <div className="w-full h-full flex items-center justify-center text-lg">👤</div>
    )}
  </div>
);

const SpinRanking = () => {
  const [ranking, setRanking] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    axios.get(`${API}/roulette/spin-ranking`)
      .then((r) => setRanking(r.data.ranking || []))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded) return <p className="text-center text-xs text-blue-300/70 py-4">Carregando ranking...</p>;
  if (ranking.length === 0) {
    return (
      <p className="text-center text-xs text-blue-300/70 py-4" data-testid="spin-ranking-empty">
        Ninguém girou ainda — seja o primeiro do ranking! 🏆
      </p>
    );
  }

  const rest = ranking.slice(3);
  return (
    <div data-testid="spin-ranking">
      {/* Pódio TOP 3 */}
      <div className="flex justify-center items-end gap-2 py-2" data-testid="spin-ranking-podium">
        {ranking[1] && (
          <div className="flex flex-col items-center flex-1 max-w-[90px]">
            <RankAvatar entry={ranking[1]} size="w-10 h-10" border="border-gray-400" />
            <p className="text-gray-300 text-[10px] truncate w-full text-center font-medium">{ranking[1].name}</p>
            <p className="text-gray-400 text-[10px] font-bold">{fmtN(ranking[1].spins)} giros</p>
            <div className="mt-1 bg-gradient-to-t from-gray-600 to-gray-400 w-full h-14 rounded-t-lg flex items-center justify-center">
              <span className="text-2xl">🥈</span>
            </div>
          </div>
        )}
        {ranking[0] && (
          <div className="flex flex-col items-center flex-1 max-w-[100px]">
            <RankAvatar entry={ranking[0]} size="w-12 h-12" border="border-yellow-400" />
            <p className="text-yellow-300 text-[10px] truncate w-full text-center font-bold">{ranking[0].name}</p>
            <p className="text-yellow-400 text-[10px] font-bold">{fmtN(ranking[0].spins)} giros</p>
            <div className="mt-1 bg-gradient-to-t from-yellow-600 to-amber-400 w-full h-20 rounded-t-lg flex items-center justify-center shadow-lg shadow-yellow-500/30">
              <span className="text-3xl animate-pulse">🥇</span>
            </div>
          </div>
        )}
        {ranking[2] && (
          <div className="flex flex-col items-center flex-1 max-w-[80px]">
            <RankAvatar entry={ranking[2]} size="w-9 h-9" border="border-amber-600" />
            <p className="text-amber-400 text-[10px] truncate w-full text-center font-medium">{ranking[2].name}</p>
            <p className="text-amber-500 text-[10px] font-bold">{fmtN(ranking[2].spins)} giros</p>
            <div className="mt-1 bg-gradient-to-t from-amber-700 to-amber-500 w-full h-10 rounded-t-lg flex items-center justify-center">
              <span className="text-xl">🥉</span>
            </div>
          </div>
        )}
      </div>

      {/* 4º em diante */}
      {rest.length > 0 && (
        <div className="border-t border-blue-500/30 pt-2 mt-2 space-y-1.5 max-h-52 overflow-y-auto pr-1 custom-scrollbar"
          data-testid="spin-ranking-list">
          {rest.map((e) => (
            <div key={e.rank} className="flex items-center gap-2 rounded-lg bg-white/5 border border-white/10 px-3 py-1.5"
              data-testid="spin-ranking-item">
              <span className="text-blue-300 font-bold text-xs w-6 text-center shrink-0">{e.rank}º</span>
              <div className="w-7 h-7 rounded-full overflow-hidden border border-blue-500/50 bg-blue-900 shrink-0">
                {e.avatar ? (
                  <img src={e.avatar} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-xs">👤</div>
                )}
              </div>
              <p className="text-white text-xs truncate flex-1">
                {e.name} <span className="text-blue-400 text-[10px]">({e.kako_masked})</span>
              </p>
              <p className="text-blue-200 text-xs font-bold shrink-0">{fmtN(e.spins)} giros</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const SocialPanel = () => {
  const [tab, setTab] = useState('live');
  const tabBtn = (key, label, testId) => (
    <button onClick={() => setTab(key)} data-testid={testId}
      className={`flex-1 rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${tab === key
        ? 'bg-blue-500 text-white shadow-md shadow-blue-500/40'
        : 'text-blue-300 hover:text-white'}`}>
      {label}
    </button>
  );
  return (
    <div className="mt-4 w-full max-w-sm bg-[#0b1240]/85 border border-blue-500/40 rounded-2xl p-4"
      data-testid="roulette-social-panel">
      <JackpotMarquee />
      <div className="flex gap-1 bg-blue-950/70 p-1 rounded-full mb-3">
        {tabBtn('live', '🎉 Ganhadores ao vivo', 'roulette-tab-live')}
        {tabBtn('ranking', '🏆 Ranking de giros', 'roulette-tab-ranking')}
      </div>
      <div className={tab === 'live' ? '' : 'hidden'}><WinnersTicker /></div>
      <div className={tab === 'ranking' ? '' : 'hidden'}><SpinRanking /></div>
    </div>
  );
};

const JackpotDisplay = ({ value }) => {
  const [shown, setShown] = useState(value);
  const [flash, setFlash] = useState(false);
  const shownRef = useRef(value);
  const rafRef = useRef(null);

  useEffect(() => {
    const from = shownRef.current, to = Number(value || 0);
    if (from === to) return undefined;
    if (to > from) {
      setFlash(true);
      setTimeout(() => setFlash(false), 1400);
    }
    const t0 = performance.now(), dur = 900;
    const step = (t) => {
      const p = Math.min((t - t0) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      const v = Math.round(from + (to - from) * eased);
      shownRef.current = v;
      setShown(v);
      if (p < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [value]);

  return (
    <div className="mt-3 w-[min(72vw,300px)] text-center" data-testid="roulette-jackpot-display">
      <div className={`relative rounded-2xl px-5 pt-2 pb-3 overflow-visible jackpot-glow ${flash ? 'jackpot-flash' : ''}`}
        style={{
          background: 'linear-gradient(180deg, #1a2c7a 0%, #0b1240 100%)',
          border: '2px solid rgba(255,213,74,.75)',
          boxShadow: '0 0 26px rgba(255,200,50,.25), inset 0 1px 0 rgba(255,255,255,.15)',
        }}>
        <div className="absolute inset-0 rounded-2xl pointer-events-none"
          style={{ background: 'radial-gradient(60% 120% at 50% 0%, rgba(255,213,74,.14), transparent 70%)' }} />
        <img src={`${A}/jackpot_title.png`} alt="Jackpot" draggable={false}
          className="mx-auto w-[80%] mt-0.5 drop-shadow-[0_6px_16px_rgba(0,0,0,.45)]" />
        <div className="flex items-center justify-center gap-1.5 mt-1">
          <img src={`${A}/moeda_amarela.png`} alt="" className="w-6 h-6 drop-shadow" />
          <span className="text-2xl sm:text-3xl font-black tabular-nums text-yellow-300"
            data-testid="roulette-jackpot-value"
            style={{ textShadow: '0 2px 0 #8a5a00, 0 5px 14px rgba(0,0,0,.55)' }}>
            {fmtN(shown)}
          </span>
        </div>
      </div>
    </div>
  );
};

const EndCountdown = ({ endsAt }) => {
  const [left, setLeft] = useState(() => new Date(endsAt) - Date.now());
  useEffect(() => {
    const t = setInterval(() => setLeft(new Date(endsAt) - Date.now()), 1000);
    return () => clearInterval(t);
  }, [endsAt]);
  if (left <= 0) return null;
  const d = Math.floor(left / 86400000);
  const pad = (n) => String(n).padStart(2, '0');
  const h = pad(Math.floor(left / 3600000) % 24);
  const m = pad(Math.floor(left / 60000) % 60);
  const s = pad(Math.floor(left / 1000) % 60);
  return (
    <div className="mt-2 flex items-center justify-center gap-2 rounded-full px-4 py-1.5"
      style={{ background: 'rgba(0,0,30,.45)', border: '1px solid rgba(255,213,74,.4)', backdropFilter: 'blur(4px)' }}
      data-testid="roulette-end-countdown">
      <Clock className="w-4 h-4 text-yellow-300" />
      <span className="text-xs text-blue-100">O evento encerra em</span>
      <span className="text-sm font-black tabular-nums text-yellow-300" data-testid="roulette-end-countdown-value">
        {d > 0 ? `${d}d ` : ''}{h}:{m}:{s}
      </span>
    </div>
  );
};

const SectorLabel = ({ angle, src, alt, scale = 1 }) => (
  <div className="absolute inset-0 pointer-events-none" style={{ transform: `rotate(${angle}deg)` }}>
    <div className="absolute left-1/2 -translate-x-1/2 flex items-center justify-center"
      style={{ top: '13.5%', width: '30%', height: '20%' }}>
      <img src={src} alt={alt} draggable={false}
        className="max-w-full max-h-full object-contain drop-shadow-[0_3px_8px_rgba(0,0,0,.6)]"
        style={scale !== 1 ? { transform: `scale(${scale})` } : undefined} />
    </div>
  </div>
);

const HistoryDialog = ({ open, onOpenChange, spins }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-md bg-[#0b1240] border-blue-800 text-white" data-testid="roulette-history-dialog">
      <DialogHeader>
        <DialogTitle className="text-blue-100 flex items-center gap-2">
          <History className="w-5 h-5 text-yellow-400" /> Meus giros
        </DialogTitle>
        <DialogDescription className="text-blue-400 text-xs">Seus últimos giros na roleta</DialogDescription>
      </DialogHeader>
      <ScrollArea className="max-h-[55vh] pr-2">
        {spins.length === 0 && (
          <p className="text-sm text-blue-300 py-6 text-center">Você ainda não girou a roleta.</p>
        )}
        <div className="space-y-2">
          {spins.map((s) => {
            const r = RESULT_LABEL[s.result] || RESULT_LABEL.LOSE;
            const f = s.yellow_prize > 0 ? FULFILL_LABEL[s.fulfillment_status] : null;
            return (
              <div key={s.spin_id} className="flex items-center justify-between rounded-lg bg-white/5 border border-white/10 px-3 py-2"
                data-testid="roulette-history-item">
                <div>
                  <p className={`text-sm font-bold ${s.status === 'FAILED' ? 'text-orange-300' : r.cls}`}>
                    {s.status === 'FAILED' ? 'Giro reembolsado' : r.txt}
                  </p>
                  <p className="text-[11px] text-blue-300">{fmtDate(s.created_at)} · −{s.blue_cost} 💙</p>
                </div>
                <div className="text-right">
                  {s.yellow_prize > 0 && (
                    <p className="text-sm font-black text-yellow-300">+{fmtN(s.yellow_prize)}</p>
                  )}
                  {f && <span className={`text-[10px] px-1.5 py-0.5 rounded ${f.cls}`}>{f.txt}</span>}
                </div>
              </div>
            );
          })}
        </div>
      </ScrollArea>
    </DialogContent>
  </Dialog>
);

const RulesDialog = ({ open, onOpenChange, state }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-md bg-[#0b1240] border-blue-800 text-white" data-testid="roulette-rules-dialog">
      <DialogHeader>
        <DialogTitle className="text-blue-100 flex items-center gap-2">
          <HelpCircle className="w-5 h-5 text-yellow-400" /> Regras do evento
        </DialogTitle>
        <DialogDescription className="text-blue-400 text-xs">Como funciona a Roleta das Azulzinhas</DialogDescription>
      </DialogHeader>
      <div className="space-y-3 text-sm text-blue-100">
        <div className="flex items-start gap-2.5">
          <img src={`${A}/azulzinha.png`} alt="" className="w-8 h-8 mt-0.5" />
          <p>A cada <b>recarga paga</b>, você acumula <b>0,5%</b> do valor em <b>Azulzinhas 💙</b>. Ex.: 100.000 moedas recarregadas = 500 Azulzinhas.</p>
        </div>
        <div className="flex items-start gap-2.5">
          <Sparkles className="w-6 h-6 text-yellow-400 shrink-0 mt-0.5" />
          <p>Cada giro custa <b>{fmtN(state?.spin_cost || 500)} Azulzinhas</b>. Prêmios: <b className="text-emerald-300">2X = {fmtN(state?.prize_2x || 1000)}</b> ou <b className="text-yellow-300">100X = {fmtN(state?.prize_100x || 50000)}</b> moedas KAKO, creditadas automaticamente na sua conta.</p>
        </div>
        <div className="flex items-start gap-2.5">
          <img src={`${A}/moeda_amarela.png`} alt="" className="w-8 h-8 mt-0.5" />
          <p>Prêmios em moedas KAKO são <b>creditados automaticamente</b> na conta do ganhador após o giro.</p>
        </div>
        <p className="text-[11px] text-blue-400">Azulzinhas são créditos promocionais de participação, sem valor de troca direta. É necessário ter conta verificada para girar.</p>
      </div>
    </DialogContent>
  </Dialog>
);

export default function RoulettePage() {
  const navigate = useNavigate();
  const wheelRef = useRef(null);
  const [state, setState] = useState(null);
  const [balance, setBalance] = useState(null);
  const [logged, setLogged] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pressed, setPressed] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showRules, setShowRules] = useState(false);

  const token = localStorage.getItem('customer_token');
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const loadState = useCallback(async () => {
    try { setState((await axios.get(`${API}/roulette/state`)).data); } catch { setState({ active: false }); }
  }, []);

  const loadMe = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API}/roulette/me`, auth);
      setBalance(r.data.blue_balance); setLogged(true);
    } catch { setLogged(false); }
  }, [token]);

  const loadHistory = useCallback(async () => {
    if (!token) return;
    try { setHistory((await axios.get(`${API}/roulette/history`, auth)).data.spins); } catch { /* silencioso */ }
  }, [token]);

  useEffect(() => { loadState(); loadMe(); loadHistory(); }, [loadState, loadMe, loadHistory]);

  // jackpot ao vivo: atualiza estado periodicamente
  useEffect(() => {
    const t = setInterval(loadState, 30000);
    return () => clearInterval(t);
  }, [loadState]);

  // ==================== SOM (música + efeitos separados) ====================
  const legacyMuted = localStorage.getItem('roleta_muted') === '1';
  const [musicMuted, setMusicMuted] = useState(() =>
    localStorage.getItem('roleta_music_muted') != null
      ? localStorage.getItem('roleta_music_muted') === '1' : legacyMuted);
  const [fxMuted, setFxMuted] = useState(() =>
    localStorage.getItem('roleta_fx_muted') != null
      ? localStorage.getItem('roleta_fx_muted') === '1' : legacyMuted);
  const [musicVol, setMusicVol] = useState(() => {
    const v = parseFloat(localStorage.getItem('roleta_music_vol'));
    return Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0.25;
  });
  const [soundOpen, setSoundOpen] = useState(false);
  const musicMutedRef = useRef(musicMuted);
  const fxMutedRef = useRef(fxMuted);
  const audioRef = useRef(null);
  const tickTimer = useRef(null);
  const bgMusicRef = useRef(null);
  const jackpotTrackRef = useRef(null);
  const fxAudioRef = useRef({});

  // ===== Pré-carregamento de assets de resultado (imagens + áudios) =====
  // Garante que efeito visual e som do prêmio apareçam INSTANTANEAMENTE, sem download na hora.
  useEffect(() => {
    const imgs = ['reward_100x.png', 'reward_2x.png', 'try_again.png', 'moeda_amarela.png']
      .map((f) => {
        const im = new Image();
        im.src = `${A}/${f}`;
        if (im.decode) im.decode().catch(() => {});
        return im;
      });
    // Áudios: baixa via fetch p/ memória (blob) — preload de <audio> é ignorado em vários navegadores
    const files = { jackpot: 'jackpot_win.mp3', win2x: 'win2x.mp3', lose: 'lose.mp3' };
    const blobUrls = [];
    Object.entries(files).forEach(([key, f]) => {
      const a = new Audio(`${A}/${f}`);
      a.preload = 'auto';
      a.load();
      fxAudioRef.current[key] = a;
      fetch(`${A}/${f}`)
        .then((r) => (r.ok ? r.blob() : Promise.reject()))
        .then((b) => {
          const u = URL.createObjectURL(b);
          blobUrls.push(u);
          a.src = u;
          a.load();
        })
        .catch(() => {});
    });
    return () => {
      imgs.forEach((im) => { im.src = ''; });
      Object.values(fxAudioRef.current).forEach((a) => { a.pause(); a.src = ''; });
      fxAudioRef.current = {};
      blobUrls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, []);

  useEffect(() => {
    musicMutedRef.current = musicMuted;
    localStorage.setItem('roleta_music_muted', musicMuted ? '1' : '0');
  }, [musicMuted]);
  useEffect(() => {
    fxMutedRef.current = fxMuted;
    localStorage.setItem('roleta_fx_muted', fxMuted ? '1' : '0');
  }, [fxMuted]);
  useEffect(() => {
    localStorage.setItem('roleta_music_vol', String(musicVol));
    if (bgMusicRef.current) bgMusicRef.current.volume = musicVol;
  }, [musicVol]);
  useEffect(() => () => { if (tickTimer.current) clearTimeout(tickTimer.current); }, []);

  // ===== Música de fundo — playlist em loop =====
  useEffect(() => {
    const playlist = ['roleta_bg.mp3', 'roleta_bg2.mp3'];
    let track = 0;
    const audio = new Audio(`${A}/${playlist[track]}`);
    audio.volume = musicVol;
    bgMusicRef.current = audio;
    // ao terminar uma faixa, toca a próxima da playlist (ciclo infinito)
    audio.onended = () => {
      track = (track + 1) % playlist.length;
      audio.src = `${A}/${playlist[track]}`;
      if (!musicMutedRef.current && !jackpotTrackRef.current) audio.play().catch(() => {});
    };
    const tryPlay = () => { audio.play().catch(() => {}); };
    if (!musicMutedRef.current) tryPlay();
    // autoplay pode ser bloqueado — inicia na 1ª interação
    const unlock = () => { if (!musicMutedRef.current && !jackpotTrackRef.current) tryPlay(); };
    window.addEventListener('pointerdown', unlock);
    window.addEventListener('keydown', unlock);
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
      audio.onended = null;
      audio.pause();
      audio.src = '';
      bgMusicRef.current = null;
      if (jackpotTrackRef.current) { jackpotTrackRef.current.pause(); jackpotTrackRef.current = null; }
    };
  }, []);
  useEffect(() => {
    const audio = bgMusicRef.current;
    if (!audio) return;
    if (musicMuted) audio.pause();
    else if (!jackpotTrackRef.current || jackpotTrackRef.current.ended || jackpotTrackRef.current.paused) {
      audio.play().catch(() => {});
    }
  }, [musicMuted]);
  useEffect(() => {
    if (fxMuted && jackpotTrackRef.current) {
      jackpotTrackRef.current.pause();
      jackpotTrackRef.current = null;
      if (!musicMutedRef.current) bgMusicRef.current?.play().catch(() => {});
    }
  }, [fxMuted]);

  // Faixa especial do Jackpot: pausa a música de fundo e retoma ao terminar
  const playJackpotTrack = () => {
    if (fxMutedRef.current) return;
    bgMusicRef.current?.pause();
    const win = fxAudioRef.current.jackpot || new Audio(`${A}/jackpot_win.mp3`);
    win.currentTime = 0;
    win.volume = 0.6;
    jackpotTrackRef.current = win;
    win.onended = () => {
      jackpotTrackRef.current = null;
      if (!musicMutedRef.current) bgMusicRef.current?.play().catch(() => {});
    };
    win.play().catch(() => {});
  };

  const getCtx = () => {
    if (!audioRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) audioRef.current = new AC();
    }
    if (audioRef.current?.state === 'suspended') audioRef.current.resume();
    return audioRef.current;
  };

  const beep = (freq, t0, dur, type = 'triangle', gain = 0.12) => {
    const ctx = getCtx();
    if (!ctx || fxMutedRef.current) return;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = type; o.frequency.value = freq;
    g.gain.setValueAtTime(gain, ctx.currentTime + t0);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + t0 + dur);
    o.connect(g); g.connect(ctx.destination);
    o.start(ctx.currentTime + t0); o.stop(ctx.currentTime + t0 + dur + 0.05);
  };

  const stopTicks = () => {
    if (tickTimer.current) { clearTimeout(tickTimer.current); tickTimer.current = null; }
  };

  const startTicks = () => {
    stopTicks();
    const loop = () => { beep(1500, 0, 0.03, 'square', 0.045); tickTimer.current = setTimeout(loop, 85); };
    loop();
  };

  const decelerateTicks = (totalMs) => {
    stopTicks();
    let delay = 85, elapsed = 0;
    const step = () => {
      beep(1500, 0, 0.03, 'square', 0.045);
      elapsed += delay; delay = Math.min(delay * 1.17, 430);
      if (elapsed < totalMs - 300) tickTimer.current = setTimeout(step, delay);
    };
    step();
  };

  const playResultSound = (res) => {
    if (res === 'WIN_100X') {
      playJackpotTrack();
    } else if (res === 'WIN_2X') {
      // Som de vitória 2X (mp3 do usuário) — música de fundo continua tocando
      if (!fxMutedRef.current) {
        const win2x = fxAudioRef.current.win2x || new Audio(`${A}/win2x.mp3`);
        win2x.currentTime = 0;
        win2x.volume = 0.55;
        win2x.play().catch(() => {});
      }
    } else {
      // Som de derrota (mp3 do usuário) — música de fundo continua tocando
      if (!fxMutedRef.current) {
        const lose = fxAudioRef.current.lose || new Audio(`${A}/lose.mp3`);
        lose.currentTime = 0;
        lose.volume = 0.5;
        lose.play().catch(() => {});
      }
    }
  };
  // =========================================================================


  // Compartilhar o evento (Web Share API com fallback de copiar link)
  const shareEvent = async () => {
    const url = `${window.location.origin}/roleta`;
    const jackpot = Number(state?.jackpot_value || 0).toLocaleString('pt-BR');
    const text = state?.active
      ? `🎡 Roleta das Azulzinhas está rolando! Jackpot acumulado: ${jackpot} moedas. Recarregue, junte Azulzinhas e gire: ${url}`
      : `🎡 Conheça a Roleta das Azulzinhas! Recarregue, junte Azulzinhas e gire para ganhar moedas: ${url}`;
    if (navigator.share) {
      try { await navigator.share({ title: 'Roleta das Azulzinhas', text, url }); return; }
      catch { return; }
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Link do evento copiado! Cole onde quiser compartilhar 🎡');
    } catch {
      toast.error('Não foi possível copiar o link');
    }
  };

  const currentAngle = () => {
    const tr = window.getComputedStyle(wheelRef.current).transform;
    if (!tr || tr === 'none') return 0;
    const m = tr.match(/matrix\(([^)]+)\)/);
    if (!m) return 0;
    const [a, b] = m[1].split(',').map(parseFloat);
    const ang = Math.atan2(b, a) * (180 / Math.PI);
    return ang < 0 ? ang + 360 : ang;
  };

  const landOn = (spin) => {
    const el = wheelRef.current;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const cur = currentAngle();
    el.classList.remove('roleta-loop');
    el.style.transition = 'none';
    el.style.transform = `rotate(${cur}deg)`;
    void el.offsetHeight;
    const sector = SECTOR_ANGLE[spin.result] ?? 120;
    const jitter = Math.random() * 70 - 35;
    const turns = reduced ? 360 : 5 * 360;
    const target = cur + turns + ((360 - sector - (cur % 360) + 720) % 360) + jitter;
    const dur = reduced ? 900 : 3400;
    el.style.transition = `transform ${dur}ms cubic-bezier(0.12, 0.75, 0.15, 1)`;
    el.style.transform = `rotate(${target}deg)`;
    decelerateTicks(dur);
    setTimeout(() => {
      stopTicks();
      setResult(spin);
      playResultSound(spin.result);
      if (spin.result === 'WIN_100X' && navigator.vibrate && !reduced) {
        navigator.vibrate([200, 80, 200, 80, 500]);
      }
      if (typeof spin.blue_balance_after === 'number') setBalance(spin.blue_balance_after);
      setBusy(false);
      loadHistory();
      loadState(); // atualiza o jackpot acumulado
    }, dur + 150);
  };

  const stopLoop = () => {
    const el = wheelRef.current;
    if (el) { el.classList.remove('roleta-loop'); }
  };

  const doSpin = async () => {
    if (busy || !state?.active) return;
    if (!logged) { navigate('/conta'); return; }
    setError(''); setResult(null); setBusy(true);
    setPressed(true); setTimeout(() => setPressed(false), 160);
    getCtx(); startTicks();
    wheelRef.current.style.transition = 'none';
    wheelRef.current.classList.add('roleta-loop');
    const crid = (window.crypto?.randomUUID && window.crypto.randomUUID()) ||
      `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
    try {
      const res = await axios.post(`${API}/roulette/spin`, { client_request_id: crid }, auth);
      if (res.data.refunded) {
        stopLoop(); stopTicks(); setBusy(false);
        setError(res.data.message || 'Prêmio indisponível — giro reembolsado.');
        loadMe(); loadHistory();
        return;
      }
      landOn(res.data.spin);
    } catch (e) {
      stopLoop(); stopTicks(); setBusy(false);
      const st = e.response?.status;
      if (st === 402) setError('Azulzinhas insuficientes. Acumule 0,5% a cada recarga!');
      else if (st === 429) setError('Muitos giros seguidos — respire e tente de novo.');
      else if (st === 401 || st === 403) { setLogged(false); setError('Entre na sua conta para girar.'); }
      else setError('Não foi possível girar agora. Tente novamente.');
    }
  };

  const active = state?.active;

  return (
    <div className="min-h-screen text-white relative overflow-hidden" data-testid="roulette-page"
      style={{ background: '#060a2e' }}>
      <style>{`
        .roleta-bg { position: absolute; inset: 0; background: url('${A}/bg_desktop.jpg') center/cover no-repeat; opacity: .55; }
        @media (max-width: 640px) { .roleta-bg { background-image: url('${A}/bg_mobile.jpg'); } }
        .roleta-loop { animation: roleta-spin .55s linear infinite; }
        @keyframes roleta-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @media (prefers-reduced-motion: reduce) { .roleta-loop { animation-duration: 1.6s; } }
        .roleta-glow { position: absolute; inset: -9%; border-radius: 50%; pointer-events: none;
          opacity: 0; z-index: 0; transition: opacity 1s ease;
          background: radial-gradient(closest-side, rgba(255,213,74,0) 52%, rgba(255,213,74,.5) 71%, rgba(255,160,30,.28) 82%, transparent 100%);
          filter: blur(7px); }
        .roleta-glow-on { opacity: 1; animation: roleta-glow-pulse 1.1s ease-in-out infinite; }
        @keyframes roleta-glow-pulse {
          0%, 100% { transform: scale(1); filter: blur(7px) brightness(1); }
          50% { transform: scale(1.06); filter: blur(10px) brightness(1.4); }
        }
        @media (prefers-reduced-motion: reduce) { .roleta-glow-on { animation: none; } }
        @keyframes coin-fall {
          0% { transform: translateY(-10vh) rotate(0deg); opacity: 0; }
          8% { opacity: 1; }
          100% { transform: translateY(115vh) rotate(calc(var(--spin-dir, 1) * 540deg)); opacity: 1; }
        }
        @media (prefers-reduced-motion: reduce) { [data-testid="coin-rain"] { display: none; } }
        .jackpot-glow { animation: jackpot-ambient 2.6s ease-in-out infinite; }
        @keyframes jackpot-ambient {
          0%, 100% { box-shadow: 0 0 26px rgba(255,200,50,.25), inset 0 1px 0 rgba(255,255,255,.15); }
          50% { box-shadow: 0 0 55px rgba(255,213,74,.6), 0 0 95px rgba(255,213,74,.28), inset 0 1px 0 rgba(255,255,255,.22); }
        }
        .jackpot-glow.jackpot-flash { animation: jackpot-pulse 0.7s ease-in-out 2, jackpot-ambient 2.6s ease-in-out infinite; }
        .jackpot-flash { animation: jackpot-pulse 0.7s ease-in-out 2; }
        @keyframes jackpot-pulse {
          0%, 100% { box-shadow: 0 0 34px rgba(255,200,50,.28), inset 0 1px 0 rgba(255,255,255,.15); transform: scale(1); }
          50% { box-shadow: 0 0 60px rgba(255,213,74,.85), 0 0 110px rgba(255,213,74,.4), inset 0 1px 0 rgba(255,255,255,.3); transform: scale(1.035); }
        }
        @media (prefers-reduced-motion: reduce) { .jackpot-flash, .jackpot-glow { animation: none; } }
      `}</style>
      <div className="roleta-bg" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#060a2e]/40 via-transparent to-[#060a2e]" />

      <div className="relative z-10 max-w-3xl mx-auto px-4 pt-4 pb-14 flex flex-col items-center">
        {/* topo */}
        <div className="w-full flex items-center justify-between gap-2">
          <button onClick={() => navigate('/')} data-testid="roulette-back-button"
            className="flex items-center gap-1.5 text-sm text-blue-200 hover:text-white bg-white/10 border border-white/15 rounded-full px-3 py-1.5 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Loja
          </button>
          <div className="flex items-center gap-2">
            <div className="relative">
              <button onClick={() => setSoundOpen((o) => !o)} data-testid="roulette-mute-button"
                aria-label="Opções de som"
                className={`flex items-center justify-center w-9 h-9 rounded-full border transition-colors ${(musicMuted && fxMuted)
                  ? 'bg-white/5 border-white/10 text-blue-400'
                  : 'bg-white/10 border-white/15 text-yellow-400 hover:text-yellow-300'}`}>
                {(musicMuted && fxMuted) ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              </button>
              {soundOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setSoundOpen(false)} />
                  <div className="absolute right-0 top-11 z-50 w-64 rounded-2xl p-4 shadow-2xl text-left"
                    style={{ background: 'rgba(8,14,50,.96)', border: '1px solid rgba(255,213,74,.35)', backdropFilter: 'blur(8px)' }}
                    data-testid="roulette-sound-panel">
                    <p className="mb-3 text-xs font-black uppercase tracking-wide text-yellow-300">🎵 Opções de som</p>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm text-blue-100">Música de fundo</span>
                      <Switch checked={!musicMuted} onCheckedChange={(v) => setMusicMuted(!v)}
                        data-testid="sound-music-switch" />
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <VolumeX className="w-3.5 h-3.5 text-blue-300 shrink-0" />
                      <input type="range" min="0" max="100" value={Math.round(musicVol * 100)}
                        disabled={musicMuted}
                        onChange={(e) => setMusicVol(Number(e.target.value) / 100)}
                        className="w-full accent-yellow-400 disabled:opacity-40"
                        data-testid="sound-music-volume" />
                      <Volume2 className="w-3.5 h-3.5 text-blue-300 shrink-0" />
                    </div>
                    <div className="mt-3 flex items-center justify-between gap-2">
                      <span className="text-sm text-blue-100">Efeitos sonoros</span>
                      <Switch checked={!fxMuted} onCheckedChange={(v) => setFxMuted(!v)}
                        data-testid="sound-fx-switch" />
                    </div>
                  </div>
                </>
              )}
            </div>
            <button onClick={() => { loadHistory(); setShowHistory(true); }} data-testid="roulette-history-button"
              className="flex items-center gap-1.5 text-sm text-blue-100 hover:text-white bg-white/10 border border-white/15 rounded-full px-3 py-1.5 transition-colors">
              <History className="w-4 h-4 text-yellow-400" /> Histórico
            </button>
            <button onClick={() => setShowRules(true)} data-testid="roulette-rules-button"
              className="flex items-center gap-1.5 text-sm text-blue-100 hover:text-white bg-white/10 border border-white/15 rounded-full px-3 py-1.5 transition-colors">
              <HelpCircle className="w-4 h-4 text-yellow-400" /> Regras
            </button>
            <button onClick={shareEvent} data-testid="roulette-share-button"
              className="flex items-center gap-1.5 text-sm text-blue-100 hover:text-white bg-white/10 border border-white/15 rounded-full px-3 py-1.5 transition-colors">
              <Share2 className="w-4 h-4 text-yellow-400" /> Compartilhar
            </button>
          </div>
        </div>

        {/* HUD saldo */}
        <div className="w-full flex justify-center mt-3">
          <div className="flex items-center gap-2.5 bg-[#0b1240]/80 border border-blue-500/40 rounded-full pl-2 pr-5 py-1.5 shadow-lg shadow-blue-900/40"
            data-testid="roulette-balance-hud">
            <img src={`${A}/azulzinha.png`} alt="Azulzinha" className="w-9 h-9 drop-shadow" />
            <div className="leading-tight">
              <p className="text-[10px] uppercase tracking-widest text-blue-300">Suas Azulzinhas</p>
              <p className="text-xl font-black text-white" data-testid="roulette-balance-value">
                {logged ? fmtN(balance) : '—'}
              </p>
            </div>
          </div>
        </div>

        {/* título */}
        <h1 className="mt-4 text-center font-black tracking-tight text-3xl sm:text-4xl"
          style={{ color: '#ffd94a', textShadow: '0 3px 0 #b06400, 0 8px 24px rgba(0,0,0,.6)' }}>
          ROLETA DAS <span className="text-white" style={{ textShadow: '0 3px 0 #1440a0, 0 8px 24px rgba(0,0,0,.6)' }}>AZULZINHAS</span>
        </h1>

        {/* 💎 JACKPOT acumulado — acima da roleta */}
        {active && <JackpotDisplay value={state?.jackpot_value || 0} />}
        {active && state?.ends_at && <EndCountdown endsAt={state.ends_at} />}

        {/* roleta */}
        <div className="relative mt-5 w-[min(86vw,420px)] aspect-square select-none">
          <div className={`roleta-glow ${busy ? 'roleta-glow-on' : ''}`} aria-hidden="true"
            data-testid="roulette-spin-glow" />
          <img src={`${A}/pointer.png`} alt="" draggable={false}
            className="absolute left-1/2 -translate-x-1/2 -top-[5%] w-[21%] z-20 drop-shadow-xl" />
          <div ref={wheelRef} className="absolute inset-0 will-change-transform" data-testid="roulette-wheel">
            <img src={`${A}/disc.png`} alt="Roleta" draggable={false} className="w-full h-full" />
            <SectorLabel angle={0} src={`${A}/label_100x.png`} alt="100X" />
            <SectorLabel angle={120} src={`${A}/label_tente.png`} alt="Tente novamente" />
            <SectorLabel angle={240} src={`${A}/label_2x.png`} alt="2X" scale={1.35} />
          </div>
        </div>

        {/* botão girar */}
        <div className="mt-2 flex flex-col items-center">
          {!active ? (
            <div className="text-center bg-[#0b1240]/85 border border-blue-500/40 rounded-2xl px-6 py-4 mt-2"
              data-testid="roulette-inactive-notice">
              <p className="font-bold text-blue-100">A roleta não está ativa no momento.</p>
              <p className="text-sm text-blue-300 mt-1">Continue recarregando — em breve novidades! ✨</p>
            </div>
          ) : (
            <>
              {logged && (
                <button onClick={doSpin}
                  disabled={busy || (typeof balance === 'number' && balance < (state?.spin_cost || 500))}
                  data-testid="roulette-spin-button"
                  className="relative transition-transform active:scale-95 disabled:opacity-60 disabled:grayscale-[35%]"
                  style={{ width: 'min(60vw, 280px)' }}>
                  <img src={pressed || busy ? `${A}/btn_pressed.png` : `${A}/btn_default.png`}
                    alt="Girar" draggable={false} className="w-full" />
                  {busy && (
                    <Loader2 className="absolute inset-0 m-auto w-8 h-8 animate-spin text-white drop-shadow" />
                  )}
                </button>
              )}
              <p className="text-sm text-blue-200 mt-1 flex items-center gap-1.5" data-testid="roulette-spin-cost">
                <img src={`${A}/azulzinha.png`} alt="" className="w-5 h-5" />
                <b className="text-white">{fmtN(state?.spin_cost)}</b> Azulzinhas = 1 giro
              </p>
              {logged && typeof balance === 'number' && balance < (state?.spin_cost || 500) && (
                <p className="mt-2 text-xs text-blue-300" data-testid="roulette-insufficient-hint">
                  Saldo insuficiente — recarregue para acumular Azulzinhas 💙
                </p>
              )}
              {!logged && (
                <button onClick={() => navigate('/conta')} data-testid="roulette-login-cta"
                  className="mt-3 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-bold rounded-full px-6 py-2.5 shadow-lg transition-colors">
                  Entrar / Criar conta para girar
                </button>
              )}
            </>
          )}
          {error && (
            <p className="mt-3 text-sm font-semibold text-orange-300 bg-orange-950/60 border border-orange-500/40 rounded-lg px-4 py-2"
              data-testid="roulette-error">{error}</p>
          )}
        </div>

        {/* ganhadores ao vivo + ranking de giros */}
        {active && <SocialPanel />}
      </div>

      {/* overlay de resultado — SEMPRE montado (imagens ficam decodificadas na memória
          e o prêmio ESTOURA na tela instantaneamente, sem atraso de carregamento) */}
      <div className={`fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm px-6 ${result ? '' : 'invisible pointer-events-none'}`}
        data-testid={result ? 'roulette-result-overlay' : undefined} aria-hidden={!result}>
        {result?.result === 'WIN_100X' && <CoinRain />}
        <div className="text-center max-w-sm w-full">
          <div className="relative w-full">
            {['WIN_100X', 'WIN_2X', 'LOSE'].map((k) => (
              <img key={k} src={RESULT_IMG[k]} alt={k} draggable={false}
                className={`w-full drop-shadow-2xl ${result?.result === k
                  ? 'animate-in zoom-in-90 duration-300'
                  : 'absolute inset-0 opacity-0 pointer-events-none'}`} />
            ))}
          </div>
          {result && (result.yellow_prize > 0 ? (
            <p className="mt-2 text-sm text-blue-100">
              🎉 <b className="text-yellow-300">{fmtN(result.yellow_prize)} moedas</b> serão creditadas
              automaticamente na sua conta KAKO!
            </p>
          ) : (
            <p className="mt-2 text-sm text-blue-200">Continue recarregando para acumular mais Azulzinhas 💙</p>
          ))}
          <button onClick={() => setResult(null)} data-testid="roulette-result-close"
            className="mt-4 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold rounded-full px-8 py-2.5 shadow-lg transition-colors">
            Continuar
          </button>
        </div>
      </div>

      <HistoryDialog open={showHistory} onOpenChange={setShowHistory} spins={history} />
      <RulesDialog open={showRules} onOpenChange={setShowRules} state={state} />
    </div>
  );
}
